import type { MicrositeTheme } from '~/model';

export const css_slow = '500ms ease';
export const css_quick = '250ms ease';
type css_colors = 'white' | 'black' | 'green' | 'brand' | 'red';

type Props = { theme: MicrositeTheme };

export function css_if<P extends Props, IT, IF>(condition: keyof P, ifTrue: IT, ifFalse: IF) {
	return (props: P) => (props[condition] ? ifTrue : ifFalse);
}

export function css_prop<P extends Props>(condition: keyof P, fallback: string = 'auto') {
	return (props: P) => props[condition] ?? fallback;
}

export const css_letter = (size: 'sm' | 'md' | 'lg') => {
	return (props: Props) => props.theme.font.spacing[size];
};

export const css_edge =
	(multiply = 1) =>
	(props: Props) =>
		`calc(${css_gap('grid')(props)} * ${multiply})`;

export const css_text = (tone: 'weak' | 'strong' = 'strong', color: css_colors = 'black') => {
	return (props: Props) => props.theme.color[color].text[tone];
};

export const css_stroke = (tone: 'weak' | 'strong' = 'weak', color: css_colors = 'black') => {
	return (props: Props) => props.theme.color[color].stroke[tone];
};

export const css_vh = (vh = 100) => {
	return (props: Props) => ((props.theme.zoom ?? 0 > 0) ? `min(${props.theme.zoom}px, ${vh}vh)` : `${vh}vh`);
};

export const css_fill = (color?: css_colors) => {
	return (props: Props) => props.theme.color[color ?? props.theme.color.fill].fill;
};

export const css_background = (color: css_colors = 'white') => {
	return (props: Props) => props.theme.color[color].text.strong;
};

export const css_gap = (type: 'grid' | 'content' | 'large' | 'section' = 'content', divide = 1) => {
	return (props: Props) => `calc(${props.theme.gap[type]} / ${divide})`;
};

export const css_radius = (props: Props) => props.theme.radius;

export const css_rail = (props: Props) => props.theme.rail.width;

export const css_small_down = (props: Props) => `max-width: ${props.theme.viewport.small}px`;

export const css_small_up = (props: Props) => `min-width: ${props.theme.viewport.small + 1}px`;

export const css_xlarge = (props: Props) => `min-width: ${props.theme.viewport.xlarge}px`;

export const css_medium_down = (props: Props) => `max-width: ${props.theme.viewport.medium - 1}px`;

export const css_medium = (props: Props) => `(${css_medium_up(props)}) and (max-width: ${props.theme.viewport.large - 1}px)`;

export const css_large_up = (props: Props) => `min-width: ${props.theme.viewport.large}px`;

export const css_large = (props: Props) => `(${css_large_up(props)}) and (max-width: ${props.theme.viewport.xlarge - 1}px)`;

export const css_medium_up = (props: Props) => `min-width: ${props.theme.viewport.medium}px`;

export const css_rail_down = (props: Props) => `max-width: calc(599px + ${props.theme.rail.width})`;

export const css_rail_up = (props: Props) => `min-width: calc(600px + ${props.theme.rail.width})`;
